/**
 * Safe DOM Manipulation Utility
 * Prevents Content Spoofing by using secure DOM manipulation instead of innerHTML
 * OWASP A3 - Injection Prevention
 */

/**
 * Escapes HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text safe for display
 */
export const escapeHtml = (text) => {
    if (text === null || text === undefined) {
        return '';
    }
    
    const textStr = String(text);
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
        '/': '&#x2F;'
    };
    
    return textStr.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Safely sets text content of an element (prevents XSS)
 * @param {HTMLElement} element - DOM element to update
 * @param {string} text - Text content to set
 */
export const safeSetTextContent = (element, text) => {
    if (!element) return;
    element.textContent = text || '';
};

/**
 * Safely creates a text node and appends it
 * @param {HTMLElement} parent - Parent element
 * @param {string} text - Text content
 * @returns {Text} - Created text node
 */
export const safeAppendText = (parent, text) => {
    if (!parent) return null;
    const textNode = document.createTextNode(text || '');
    parent.appendChild(textNode);
    return textNode;
};

/**
 * Safely creates an element with attributes and text content
 * @param {string} tagName - HTML tag name
 * @param {object} attributes - Object with attribute key-value pairs
 * @param {string|HTMLElement} content - Text content or child element
 * @returns {HTMLElement} - Created element
 */
export const safeCreateElement = (tagName, attributes = {}, content = null) => {
    const element = document.createElement(tagName);
    
    // Set attributes safely
    Object.keys(attributes).forEach(key => {
        if (key === 'className') {
            element.className = attributes[key];
        } else if (key === 'style' && typeof attributes[key] === 'object') {
            Object.assign(element.style, attributes[key]);
        } else if (key.startsWith('data-') || key === 'id' || key === 'type' || key === 'name') {
            element.setAttribute(key, String(attributes[key]));
        } else {
            element[key] = attributes[key];
        }
    });
    
    // Set content safely
    if (content !== null) {
        if (typeof content === 'string') {
            safeAppendText(element, content);
        } else if (content instanceof HTMLElement) {
            element.appendChild(content);
        }
    }
    
    return element;
};

/**
 * Safely replaces innerHTML with textContent (prevents XSS)
 * @param {HTMLElement} element - Element to update
 * @param {string} text - Text content (will be escaped)
 */
export const safeSetContent = (element, text) => {
    if (!element) return;
    safeSetTextContent(element, text);
};

/**
 * Safely creates HTML structure from a template (with whitelisted tags)
 * @param {HTMLElement} container - Container element
 * @param {string} template - HTML template string (only whitelisted tags allowed)
 * @param {object} data - Data object for template interpolation
 */
export const safeRenderTemplate = (container, template, data = {}) => {
    if (!container) return;
    
    // Clear container safely
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }
    
    // Only allow whitelisted HTML tags
    const allowedTags = ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'button', 'input', 'label', 'br'];
    
    // Simple template parser (for basic use cases)
    // For complex templates, use React components instead
    const parser = new DOMParser();
    const doc = parser.parseFromString(template, 'text/html');
    const body = doc.body;
    
    // Validate and sanitize nodes
    const walker = document.createTreeWalker(
        body,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
        null,
        false
    );
    
    const fragment = document.createDocumentFragment();
    let node;
    
    while (node = walker.nextNode()) {
        if (node.nodeType === Node.TEXT_NODE) {
            const textNode = document.createTextNode(escapeHtml(node.textContent));
            fragment.appendChild(textNode);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            if (allowedTags.includes(tagName)) {
                const newElement = document.createElement(tagName);
                
                // Copy safe attributes only
                Array.from(node.attributes).forEach(attr => {
                    if (attr.name === 'class' || attr.name === 'id' || attr.name.startsWith('data-')) {
                        newElement.setAttribute(attr.name, escapeHtml(attr.value));
                    }
                });
                
                // Copy text content
                if (node.textContent) {
                    safeAppendText(newElement, escapeHtml(node.textContent));
                }
                
                fragment.appendChild(newElement);
            }
        }
    }
    
    container.appendChild(fragment);
};

/**
 * Safely updates element content with React-like approach
 * @param {HTMLElement} element - Element to update
 * @param {string|HTMLElement|Array} content - Content to set
 */
export const safeUpdateContent = (element, content) => {
    if (!element) return;
    
    // Clear existing content
    while (element.firstChild) {
        element.removeChild(element.firstChild);
    }
    
    if (typeof content === 'string') {
        safeAppendText(element, content);
    } else if (content instanceof HTMLElement) {
        element.appendChild(content);
    } else if (Array.isArray(content)) {
        content.forEach(item => {
            if (typeof item === 'string') {
                safeAppendText(element, item);
            } else if (item instanceof HTMLElement) {
                element.appendChild(item);
            }
        });
    }
};

/**
 * Validates and sanitizes user input before display
 * @param {string} input - User input to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {object} - {isValid: boolean, sanitized: string, error: string}
 */
export const validateAndSanitizeInput = (input, maxLength = 1000) => {
    if (input === null || input === undefined) {
        return { isValid: true, sanitized: '', error: null };
    }
    
    const inputStr = String(input);
    
    // Check length
    if (inputStr.length > maxLength) {
        return {
            isValid: false,
            sanitized: '',
            error: `Input exceeds maximum length of ${maxLength} characters`
        };
    }
    
    // Check for dangerous patterns
    const dangerousPatterns = [
        /<script/i,
        /javascript:/i,
        /on\w+\s*=/i, // Event handlers like onclick, onerror, etc.
        /<iframe/i,
        /<object/i,
        /<embed/i
    ];
    
    for (const pattern of dangerousPatterns) {
        if (pattern.test(inputStr)) {
            return {
                isValid: false,
                sanitized: '',
                error: 'Input contains potentially dangerous content'
            };
        }
    }
    
    // Sanitize by escaping HTML
    const sanitized = escapeHtml(inputStr);
    
    return {
        isValid: true,
        sanitized: sanitized,
        error: null
    };
};

